
import { useState, useCallback, useRef, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { toast } from "sonner";
import { useTransactionDuplicateCheck } from "./useTransactionDuplicateCheck";
import { useTransactionCreate } from "./useTransactionCreate";
import { validateTransactionData } from "../utils/transactionValidation";
import { supabase } from "@/integrations/supabase/client";

interface Customer {
  id: string;
  name: string;
  phone: string;
  whatsapp?: string;
  description?: string;
}

interface TransactionSubmitProps {
  transactionType: string;
  selectedCustomer: Customer | null;
  amount: string;
  date: string;
  description: string;
  name: string;
  category: string;
  paymentMethod: string;
  paymentStatus: string;
  customerName: string;
  setIsNewCustomerDialogOpen: (isOpen: boolean) => void;
  resetForm: () => void;
}

// Create a storage mechanism to prevent duplicate submissions
const recentSubmissionHashes = new Map<string, number>();
// Global flag to prevent multiple concurrent submissions
let isGlobalSubmissionInProgress = false;
// Set to track submission tokens
const activeSubmissionTokens = new Set<string>();

export const useTransactionSubmit = () => {
  const { toast: useToastHook } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastSubmitTime, setLastSubmitTime] = useState(0);
  const { checkDuplicate, setLastTransaction } = useTransactionDuplicateCheck();
  const { createTransaction } = useTransactionCreate();
  const submissionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const submissionTokenRef = useRef<string | null>(null);
  const [currentUser, setCurrentUser] = useState<any>(null);

  // Get current user on load
  useEffect(() => {
    const getUser = async () => {
      const { data } = await supabase.auth.getUser();
      if (data?.user) {
        setCurrentUser(data.user);
      }
    };
    
    getUser();
    
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      setCurrentUser(session?.user || null);
    });
    
    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, []);
  
  // Generate submission hash
  const generateSubmissionHash = useCallback((data: any): string => {
    const hashData = {
      type: data.transactionType || '',
      customer_id: data.selectedCustomer?.id || '',
      amount: parseFloat(data.amount || '0').toFixed(2),
      name: (data.name || '').trim(),
      category: (data.category || '').trim(),
      description: (data.description || '').trim(),
      payment_method: data.paymentMethod || '',
      payment_status: data.paymentStatus || '',
      due_date: data.date || '',
      // Generate a unique timestamp ID to track this particular submission attempt
      timestamp: Date.now(),
      // Include a time window to avoid false positives for legitimate similar transactions on different days
      timeWindow: Math.floor(Date.now() / (1000 * 60 * 60 * 2)) // 2-hour window
    };
    
    return JSON.stringify(hashData);
  }, []);

  // Use a callback to prevent closures capturing stale state
  const handleSubmit = useCallback(async ({
    transactionType,
    selectedCustomer,
    customerName,
    amount,
    date,
    description,
    name,
    category,
    paymentMethod,
    paymentStatus,
    setIsNewCustomerDialogOpen,
    resetForm
  }: TransactionSubmitProps) => {
    // Generate unique token for this specific submission attempt
    const submissionToken = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    
    // Global submission lock check
    if (isGlobalSubmissionInProgress) {
      console.log("Global submission lock is active, preventing duplicate");
      toast.info("Aguarde um momento, processando operação anterior");
      return { success: false, message: "Uma transação já está sendo processada" };
    }

    // Check if we're already processing this exact submission
    if (activeSubmissionTokens.has(submissionToken)) {
      console.log("Duplicate token detected, submission already in progress");
      toast.info("Esta submissão já está em andamento");
      return { success: false, message: "Submissão já em andamento" };
    }

    // Generate a submission hash for duplicate checking
    const submissionHash = generateSubmissionHash({
      transactionType,
      selectedCustomer,
      amount,
      date,
      description,
      name,
      category,
      paymentMethod,
      paymentStatus
    });
    
    // Check if we've recently submitted this exact transaction data
    const now = Date.now();
    const recentSubmission = recentSubmissionHashes.get(submissionHash);
    if (recentSubmission && (now - recentSubmission < 120000)) { // 2 minutes
      console.log("Exact duplicate submission detected via hash.");
      toast.info("Esta transação parece idêntica a uma recente");
      return { success: false, message: "Transação idêntica enviada recentemente" };
    }
    
    // Track this submission
    recentSubmissionHashes.set(submissionHash, now);
    activeSubmissionTokens.add(submissionToken);
    
    // Clean up old hashes after 5 minutes
    setTimeout(() => {
      recentSubmissionHashes.delete(submissionHash);
      activeSubmissionTokens.delete(submissionToken);
    }, 5 * 60 * 1000);
    
    // Store this token for later reference
    submissionTokenRef.current = submissionToken;
    
    // Prevent double-clicks and multiple submissions
    if (isSubmitting) {
      console.log("Submission in progress, preventing duplicate");
      return { success: false, message: "Já existe um envio em andamento" };
    }
    
    // Prevent rapid consecutive submissions
    if (now - lastSubmitTime < 3000) { // 3 seconds
      console.log("Submission too soon after last one, preventing duplicate");
      toast.info("Por favor, aguarde um momento antes de enviar outra transação");
      return { success: false, message: "Aguarde um momento antes de enviar outra transação" };
    }
    
    // Set flags and timers before async operations
    setIsSubmitting(true);
    setLastSubmitTime(now);
    isGlobalSubmissionInProgress = true; // Set global lock
    
    // Clear any previous timeout
    if (submissionTimeoutRef.current) {
      clearTimeout(submissionTimeoutRef.current);
    }
    
    // Set timeout to prevent UI from being locked forever if something fails
    submissionTimeoutRef.current = setTimeout(() => {
      if (submissionTokenRef.current === submissionToken) {
        setIsSubmitting(false);
        isGlobalSubmissionInProgress = false; // Release global lock
        activeSubmissionTokens.delete(submissionToken);
        console.log("Submission timeout expired, resetting submission state");
      }
    }, 20000); // 20 seconds max
    
    try {
      // Handle customer validation for income transactions
      if (transactionType === 'entrada') {
        const customerResult = await validateTransactionData.validateCustomer(
          customerName, 
          selectedCustomer, 
          setIsNewCustomerDialogOpen
        );
        
        if (!customerResult.success) {
          activeSubmissionTokens.delete(submissionToken);
          isGlobalSubmissionInProgress = false; // Release global lock on error
          return customerResult;
        }
      }

      // Validate amount
      const amountValidation = validateTransactionData.validateAmount(amount);
      if (!amountValidation.success) {
        toast.error(amountValidation.message);
        activeSubmissionTokens.delete(submissionToken);
        isGlobalSubmissionInProgress = false; // Release global lock on error
        return { success: false };
      }

      // Prepare transaction data
      const transactionData = {
        type: transactionType,
        customer_id: selectedCustomer?.id,
        name: transactionType === 'entrada' ? null : name,
        category: transactionType === 'despesas' ? category : null,
        description,
        amount: parseFloat(amount),
        payment_method: transactionType === 'despesas' ? 'n/a' : paymentMethod,
        payment_status: paymentStatus,
        due_date: date || null,
        // User will be tracked by RLS policies on the server
      };

      console.log("Submitting transaction:", transactionData);
      
      // Check for duplicates first
      const duplicateCheck = await checkDuplicate(transactionData);
      if (!duplicateCheck.success) {
        activeSubmissionTokens.delete(submissionToken);
        isGlobalSubmissionInProgress = false; // Release global lock on error
        return duplicateCheck;
      }
      
      // After passing duplicate check, add a small delay to prevent race conditions
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // If submission was cancelled during the delay, exit early
      if (submissionTokenRef.current !== submissionToken) {
        console.log("Submission was superseded by a newer one, abandoning");
        activeSubmissionTokens.delete(submissionToken);
        isGlobalSubmissionInProgress = false; // Release global lock on abandonment
        return { success: false };
      }
      
      // Create the transaction
      const result = await createTransaction(transactionData);
      
      if (result.success) {
        // Track this successful transaction to prevent future duplicates
        if (result.data && result.data.length > 0) {
          setLastTransaction(result.data[0].id, transactionData);
        }
        
        // Reset form on success
        resetForm();
        
        toast.success(`Transação registrada com sucesso${currentUser ? ' por ' + (currentUser.email || 'você') : ''}!`);
        
        // Dispatch events to trigger UI updates
        window.dispatchEvent(new CustomEvent('forceTransactionRefresh'));
        window.dispatchEvent(new CustomEvent('transaction-changed'));
        window.dispatchEvent(new CustomEvent('transaction-created', { 
          detail: { transaction: result.data?.[0] }
        }));
      }
      
      return result;
    } catch (error) {
      console.error('Error registering transaction:', error);
      toast.error("Erro ao registrar transação. Tente novamente.");
      return { success: false, error };
    } finally {
      // Only reset isSubmitting state if this is still the current submission
      if (submissionTokenRef.current === submissionToken) {
        // Clear the timeout
        if (submissionTimeoutRef.current) {
          clearTimeout(submissionTimeoutRef.current);
          submissionTimeoutRef.current = null;
        }
        
        // Set a small delay before allowing another submission
        setTimeout(() => {
          setIsSubmitting(false);
          submissionTokenRef.current = null;
          activeSubmissionTokens.delete(submissionToken);
          isGlobalSubmissionInProgress = false; // Release global lock after delay
        }, 5000); // Increased to 5 seconds
      }
    }
  }, [isSubmitting, lastSubmitTime, checkDuplicate, createTransaction, setLastTransaction, generateSubmissionHash, currentUser]);

  return { handleSubmit, isSubmitting };
};
